import type { GuestBooking } from '~/repositories/backend/modules/guestBookings'

// Enriched type for cards and detail page (booking data + object info from search).
export interface GuestTrip {
  id: number
  bookingNumber: string
  objectId: number
  status: string
  statusLabel: string
  images: string[]
  title: string
  location: string
  meta: string
  dateRange: string
  checkin: string
  checkout: string
  nights: number
  guests: number
  statusUrl: string
  showRate?: boolean
  hasReview: boolean
  rateUrl: string
  totalPrice: string
  objectUrl: string
  propertyType: string
  maxGuests: number
  bedrooms: number
  livingSpace: number
}

function parseDate (dateStr: string): { y: number, m: number, d: number } {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number)
  return { y, m, d }
}
function formatDayDate (theDate: string): string {
  const { y, m, d } = parseDate(theDate)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('de-DE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}
function formatDateRange (fromDate: string, toDate: string): string {
  const { y: fy, m: fm, d: fd } = parseDate(fromDate)
  const { y: ty, m: tm, d: td } = parseDate(toDate)
  const from = new Date(fy, fm - 1, fd)
  const to = new Date(ty, tm - 1, td)
  const fromDay = from.toLocaleDateString('de-DE', { day: 'numeric' })
  const toDay = to.toLocaleDateString('de-DE', { day: 'numeric' })
  const fromMonth = from.toLocaleDateString('de-DE', { month: 'short' })
  const toMonth = to.toLocaleDateString('de-DE', { month: 'short' })
  const toYear = to.toLocaleDateString('de-DE', { year: 'numeric' })

  if (fm === tm && fy === ty) {
    return `${fromDay}. – ${toDay}. ${toMonth} ${toYear}`
  }
  return `${fromDay}. ${fromMonth} – ${toDay}. ${toMonth} ${toYear}`
}

function statusLabel (status: string): string {
  switch (status) {
    case 'offen':
      return 'Offen'
    case 'abgeschlossen':
      return 'Bestätigt'
    case 'wartend_auf_zahlung':
      return 'Wartet auf Zahlung'
    default:
      return status
  }
}

function buildMeta (location: string, guests: number, nights: number): string {
  return [
    location,
    `${guests} Gäste`,
    `${nights} Nächte`
  ].filter(Boolean).join(' · ')
}

function mapBookingToTrip (
  booking: GuestBooking,
  objectMap: Map<number, Record<string, unknown>>,
  isPast: boolean
): GuestTrip {
  const obj = objectMap.get(booking.object_id)
  const title = (obj?.hausname as string) || `Objekt ${booking.object_id}`
  const images = (obj?.bilder_neu as string[]) || []
  const location = (obj?.ortsname as string) || (obj?.region as string) || ''

  return {
    id: booking.id,
    bookingNumber: booking.booking_number,
    objectId: booking.object_id,
    status: booking.status,
    statusLabel: statusLabel(booking.status),
    images,
    title,
    location,
    meta: buildMeta(location, booking.guests, booking.nights),
    dateRange: formatDateRange(booking.arrival, booking.departure),
    checkin: formatDayDate(booking.arrival),
    checkout: formatDayDate(booking.departure),
    nights: booking.nights,
    guests: booking.guests,
    statusUrl: booking.status_url,
    showRate: isPast,
    hasReview: !!booking.has_review,
    rateUrl: '/objektbewertung?onr=' + booking.object_id + '&obnr=' + booking.id,
    totalPrice: booking.total_price
      ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: booking.currency || 'EUR' }).format(booking.total_price)
      : '',
    objectUrl: (obj?.url as string) || '',
    propertyType: (obj?.haustyp as string) || '',
    maxGuests: Number(obj?.personen) || 0,
    bedrooms: Number(obj?.schlafzimmer) || 0,
    livingSpace: Number(obj?.wohnflaeche) || 0
  }
}

export function useGuestBookings () {
  const upcoming = useState<GuestTrip[]>('guest-bookings-upcoming', () => [])
  const past = useState<GuestTrip[]>('guest-bookings-past', () => [])
  const loading = useState<boolean>('guest-bookings-loading', () => false)
  const loaded = useState<boolean>('guest-bookings-loaded', () => false)

  async function fetchBookings (force = false) {
    if (loaded.value && !force) {
      return
    }

    // Prevent duplicate fetches (e.g. page + profile card loading simultaneously).
    if (loading.value) {
      return
    }

    loading.value = true

    try {
      const { $backend } = useNuxtApp()
      const response = await $backend.guestBookings.getBookingsFromClient()
      const upcomingRaw = response?.data?.upcoming || []
      const pastRaw = response?.data?.past || []

      // Collect unique object IDs and load search results for titles, images, and location.
      const objectIds = [...new Set([
        ...upcomingRaw.map(b => b.object_id),
        ...pastRaw.map(b => b.object_id)
      ])]

      const objectMap = new Map<number, Record<string, unknown>>()

      if (objectIds.length > 0) {
        const searchResponse = await $backend.search.searchFromClient({
          rows: 100,
          only_object_ids: objectIds
        })
        const searchData = searchResponse?.data as { normal_results?: { plain?: Array<Record<string, unknown>> } } | undefined
        const searchResults = searchData?.normal_results?.plain || []
        for (const obj of searchResults) {
          objectMap.set(Number(obj.nr), obj)
        }
      }

      upcoming.value = upcomingRaw.map(b => mapBookingToTrip(b, objectMap, false))
      past.value = pastRaw.map(b => mapBookingToTrip(b, objectMap, true))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load guest bookings:', error)
      upcoming.value = []
      past.value = []
    } finally {
      loading.value = false
      loaded.value = true
    }
  }

  function byId (id: number): GuestTrip | undefined {
    return upcoming.value.find(t => t.id === id) || past.value.find(t => t.id === id)
  }

  // Drops the cached bookings so the next fetch loads fresh data. Called on
  // login and logout: the cache is per app instance, not per account, and
  // must not survive an account switch (stale trips until a full reload).
  function reset () {
    upcoming.value = []
    past.value = []
    loaded.value = false
  }

  return {
    upcoming,
    past,
    loading,
    loaded,
    fetchBookings,
    byId,
    reset
  }
}
